/**
 * Coordinates top-level auto-opening modals so only one is ever visible at a
 * time. Prevents Radix's modal pointer-events lock from clashing with custom
 * modals (e.g. the PWA install prompt) and keeps the UI from stacking two
 * popups on top of each other.
 *
 * Keys currently tracked: "install", "update", "daily".
 * Any component showing one of these registers its key while visible; any
 * component wanting to show one checks `isAnyOpen()` / `isOpen(key)` first
 * and can `waitUntilFree(delay)` to stagger onto the next free slot.
 */

const openKeys = new Set();
const listeners = new Set();

// Minimum pause between one modal closing and the next one opening, so users
// don't get slammed with back-to-back popups.
export const MODAL_HANDOFF_DELAY_MS = 500;

const notify = () => {
  listeners.forEach((fn) => {
    try { fn(); } catch (_) { /* noop */ }
  });
};

export const topModalCoordinator = {
  isAnyOpen() {
    return openKeys.size > 0;
  },
  isOpen(key) {
    return openKeys.has(key);
  },
  open(key) {
    if (openKeys.has(key)) return;
    openKeys.add(key);
    notify();
  },
  close(key) {
    if (!openKeys.has(key)) return;
    openKeys.delete(key);
    notify();
  },
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  /**
   * Resolves once no top-level modal is open, after waiting an additional
   * `delay` ms (default: MODAL_HANDOFF_DELAY_MS) so the previous modal's
   * close animation can finish. Returns a cancel function.
   */
  waitUntilFree(onReady, delay = MODAL_HANDOFF_DELAY_MS) {
    let cancelled = false;
    let timer = null;

    const tryFire = () => {
      if (cancelled) return;
      if (openKeys.size > 0) return;
      timer = setTimeout(() => {
        if (cancelled) return;
        if (openKeys.size > 0) return; // re-check after delay
        onReady();
      }, delay);
    };

    const unsubscribe = topModalCoordinator.subscribe(() => {
      if (timer) { clearTimeout(timer); timer = null; }
      tryFire();
    });

    // Initial attempt
    tryFire();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  },
};
